<template>
    <div class="ui-page">
        <ui-page-header title="Credores" subtitle="Credores cadastrados no portal, com status e verificação da conta."></ui-page-header>

        <div class="ui-list-bar">
            <ui-pills v-model="dataList.filter.status" :items="statusPills" @input="applyFilters" />
        </div>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field v-model="dataList.filter.name" placeholder="Buscar pelo nome" prepend-inner-icon="mdi-magnify"
                    dense outlined hide-details class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-text-field v-model="dataList.filter.email" placeholder="E-mail" dense outlined hide-details
                    class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-text-field v-model="dataList.filter.registration" placeholder="CPF" dense outlined hide-details
                    class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
            </template>

            <v-data-table :headers="dataList.headers" :items="dataList.data" :server-items-length="dataList.count"
                :options.sync="dataList.options" :loading="dataList.loading" disable-sort hide-default-footer
                no-data-text="Nenhum usuário foi encontrado" loading-text="Carregando dados..." class="ui-clickable-rows"
                @click:row="openRow">
                <template v-slot:[`item.name`]="{ item }">
                    <div class="d-flex align-center">
                        <v-avatar :size="30" class="mr-3">
                            <img :src="'https://ui-avatars.com/api/?name=' + item.name" />
                        </v-avatar>
                        <div>
                            <div class="ui-cell-main">{{ item.name }}</div>
                            <div class="ui-cell-sub">{{ item.email }}</div>
                        </div>
                    </div>
                </template>

                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :color="item.status == 0 ? 'accent' : 'error'">{{ item.status | enum(dataset.status, "id", "name") }}</ui-badge>
                </template>

                <template v-slot:[`item.account_verified_at`]="{ item }">
                    <ui-badge :text="isFilledDate(item.account_verified_at) ? 'SIM' : 'NÃO'" :color="isFilledDate(item.account_verified_at) ? 'accent' : 'warning'" />
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                    {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Editar', icon: 'mdi-pencil-outline', disabled: !$check('User', 'PATCH'), handler: () => viewItem(item) },
                    ]" />
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.itemsPerPage"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>
    </div>
</template>

<script>

import Vue from "vue";
import VueLodash from 'vue-lodash'
import _ from 'lodash';
import axios from 'axios';

import object from "@/common/util/object"
import status from "@/common/data/user-status.data";
import userTypes from "@/common/data/user-types.data";

Vue.use(VueLodash)

export default {
    name: "UserList",

    data: () => ({
        dataset: {
            status: [{ id: "", name: "Todos" }, ...status],
            userTypes: userTypes,
            clients: [],
            units: [],
            data: {}
        },
        dataList: {
            filter: {
                name: "",
                client: "",
                unit: "",
                status: "",
                type: 1
            },
            headers: [
                { text: "Nome", value: "name" },
                { text: "Status", value: "status" },
                { text: "Verificado", value: "account_verified_at"},
                { text: "Criado em", value: "created_at" },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
            ],
            data: [],
            loading: false,
            count: 0,
            options: {
                page: 1,
                itemsPerPage: 10,
                sortBy: ['name'],
                sortDesc: [false],
            },
            footerOptions: {
                itemsPerPageText: 'Itens por página:',
                itemsPerPageOptions: [10, 20, 30, 40, 50],
                pageText: '{0}-{1} de {2}',
            }
        },
    }),

    computed: {
        // type e fixo (credor), nao conta como filtro
        hasFilters() {
            const { name, email, registration, status } = this.dataList.filter;
            return !!(name || email || registration || status !== "");
        },

        statusPills() {
            const tones = { 0: "success", 1: "neutral", 3: "danger" };
            return this.dataset.status.map((item) => ({
                value: item.id,
                text: item.name,
                tone: item.id === "" ? undefined : tones[item.id],
                count: this.dataList.filter.status === item.id ? this.dataList.count : undefined,
            }));
        },
    },

    created() {
        this.load();
    },

    methods: {

        applyFilters() {
            this.dataList.options.page = 1;
            this.load();
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        clearFilters() {
            this.dataList.filter = { name: "", client: "", unit: "", status: "", type: 1 };
            this.applyFilters();
        },

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.dataList.options.itemsPerPage = size;
        },

        pageChanged(page) {
            this.dataList.options.page = page;
            this.load();
        },

        pageSizeChanged(size) {
            this.dataList.options.itemsPerPage = size;
            this.load(true);
        },

        debounce: _.debounce(function () {
            this.reload();
        }, 500),

        reload: function () {
            this.load();
        },

        isFilledDate(value) {
            return !!value && value !== '0000-00-00 00:00:00' && value !== '0000-00-00';
        },

        async load() {

            let search = Object.fromEntries(Object.entries(this.dataList.filter).filter(([_, v]) => v || v === 0))

            search = !object.isEmpty(search) ? search : null;

            this.dataList.loading = true;

            var limit = this.dataList.options.itemsPerPage;
           
            try {
                const result = await axios.get('/api/admin/users', { params: { page: this.dataList.options.page, limit: limit, search: search } });

                this.dataList.data = result.data.content.data;
                this.dataList.count = result.data.content.paging.total;

            } catch (error) {
                console.log('error ', error)
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }

        },

        // A linha abre o detalhe so com a mesma permissao do "Editar".

        openRow(item) {

            if (this.$check('User', 'GET')) this.viewItem(item);

        },


        viewItem(item) {
            this.$router.push(`credores/${item.id}`);
        },
    },
};
</script>
